import React, { useState, useEffect } from 'react';
import {
  leadMagnetApi,
  LeadMagnet,
  CreateLeadMagnetRequest,
  strategyApi,
  StrategyResult,
} from '../services/api';
import EditableMarkdown from '../components/EditableMarkdown';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, useConfirm, EmptyState, RelatedLinks, useFocusParam, type RelatedLinkItem } from '../components/ui';
import { routeById } from '../navigation/routes';

const LeadMagnetPage: React.FC = () => {
  const { confirm } = useConfirm();
  const [leadMagnets, setLeadMagnets] = useState<LeadMagnet[]>([]);
  const [strategies, setStrategies] = useState<StrategyResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  // ?id=<Lead-Magnet>: Sprung aus der ICP-Kampagne, Eintrag gleich offen.
  const [searchParams] = useSearchParams();
  const [expandedId, setExpandedId] = useState<string | null>(() => searchParams.get('id'));
  // editContent backs the post-generation create/preview form below.
  const [editContent, setEditContent] = useState('');

  const [formData, setFormData] = useState<CreateLeadMagnetRequest>({
    name: '',
    type: 'guide',
    description: '',
    targetAudience: '',
    strategyId: '',
    hostedUrl: '',
  });

  const [generateForm, setGenerateForm] = useState({
    type: 'guide',
    strategyId: '',
    customPrompt: '',
  });

  const leadMagnetTypes = [
    { value: 'checklist', label: 'Checkliste', icon: '✓' },
    { value: 'guide', label: 'Leitfaden', icon: '📖' },
    { value: 'template', label: 'Vorlage', icon: '📄' },
    { value: 'ebook', label: 'Mini-eBook', icon: '📚' },
    { value: 'worksheet', label: 'Arbeitsblatt', icon: '📝' },
    { value: 'cheatsheet', label: 'Spickzettel', icon: '📋' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  useFocusParam('id', !loading && leadMagnets.length > 0);

  const loadData = async () => {
    try {
      setLoading(true);
      const [leadMagnetsData, strategiesData] = await Promise.all([
        leadMagnetApi.getAll(),
        strategyApi.getAll(),
      ]);
      setLeadMagnets(leadMagnetsData);
      setStrategies(strategiesData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!generateForm.type) {
      setError('Bitte Typ auswählen');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const result = await leadMagnetApi.generateContent(generateForm);

      // Pre-fill the form with generated content
      const selectedStrategy = strategies.find(s => s.id === generateForm.strategyId);
      setFormData({
        name: `${leadMagnetTypes.find(t => t.value === generateForm.type)?.label} - ${new Date().toLocaleDateString('de-DE')}`,
        type: generateForm.type,
        description: generateForm.customPrompt || '',
        targetAudience: selectedStrategy?.targetAudience || '',
        strategyId: generateForm.strategyId,
      });
      setEditContent(result.content);
      setShowForm(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Generieren des Lead-Magneten');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const created = await leadMagnetApi.create(formData);

      // Update with the generated content
      if (editContent) {
        await leadMagnetApi.update(created.id, { content: editContent });
      }

      await loadData();
      setShowForm(false);
      setEditContent('');
      setFormData({
        name: '',
        type: 'guide',
        description: '',
        targetAudience: '',
        strategyId: '',
        hostedUrl: '',
      });
      setGenerateForm({
        type: 'guide',
        strategyId: '',
        customPrompt: '',
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ title: 'Lead Magnet löschen?', confirmLabel: 'Löschen', tone: 'danger' }))) return;
    try {
      await leadMagnetApi.delete(id);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  const getTypeIcon = (type: string) => {
    return leadMagnetTypes.find(t => t.value === type)?.icon || '📄';
  };

  const getTypeLabel = (type: string) => {
    return leadMagnetTypes.find(t => t.value === type)?.label || type;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <span className="loading-text">Laden...</span>
      </div>
    );
  }

  return (
    <div className="page-container">
      <PageHeader
        title="Lead-Magnete (Whitepaper, Checklisten)"
        subtitle={routeById('li-leadmagnets')?.description}
        actions={(
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn btn-primary"
          >
            {showForm ? 'Schließen' : 'Neuen Lead Magnet erstellen'}
          </button>
        )}
      />

      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Erstelle wertvolle Lead-Magnete (Checklisten, Leitfäden, Vorlagen, etc.) auf Basis deiner Strategie-Analyse.
        Diese können als Download-Angebote zur Lead-Generierung eingesetzt werden.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Generator Form */}
      {showForm && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
          <h2 className="form-section-title">Lead Magnet generieren</h2>

          <div className="form-group">
            <label className="form-label">Typ *</label>
            <select
              value={generateForm.type}
              onChange={(e) => setGenerateForm({ ...generateForm, type: e.target.value })}
              className="form-input"
              disabled={generating}
            >
              {leadMagnetTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Strategie (optional)</label>
            <select
              value={generateForm.strategyId}
              onChange={(e) => setGenerateForm({ ...generateForm, strategyId: e.target.value })}
              className="form-input"
              disabled={generating}
            >
              <option value="">Keine Strategie auswählen</option>
              {strategies.map((strategy) => (
                <option key={strategy.id} value={strategy.id}>
                  {strategy.name}
                </option>
              ))}
            </select>
            <p style={{ fontSize: '12px', color: 'var(--sec-fg-muted)', marginTop: '4px' }}>
              Wähle eine Strategie aus, um den Lead Magnet auf Zielgruppe und Pain Points abzustimmen
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">Zusätzliche Anforderungen (optional)</label>
            <textarea
              value={generateForm.customPrompt}
              onChange={(e) => setGenerateForm({ ...generateForm, customPrompt: e.target.value })}
              placeholder="z.B. Fokus auf spezielle Themen, bestimmte Struktur, etc."
              rows={3}
              className="form-textarea"
              disabled={generating}
            />
          </div>

          <button
            onClick={handleGenerate}
            disabled={generating}
            className="btn btn-success"
            style={{ marginBottom: '16px' }}
          >
            {generating ? 'Generiere Inhalt...' : 'Lead Magnet generieren'}
          </button>

          {/* Save Form (shown after generation) */}
          {editContent && (
            <form onSubmit={handleSave} style={{ marginTop: '24px', paddingTop: '24px', borderTop: '2px solid var(--sec-border)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                Generierter Inhalt (kann angepasst werden)
              </h3>

              <div className="form-group">
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Beschreibung</label>
                <input
                  type="text"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Kurze Beschreibung des Lead-Magneten"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Gehostete URL (optional)</label>
                <input
                  type="url"
                  value={formData.hostedUrl || ''}
                  onChange={(e) => setFormData({ ...formData, hostedUrl: e.target.value })}
                  placeholder="https://… — Landing-/Download-Link für die {{leadMagnet}}-Variable"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Inhalt (Markdown) *</label>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={15}
                  className="form-textarea"
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>

              <div className="btn-group">
                <button type="submit" disabled={saving} className="btn btn-success">
                  {saving ? 'Speichern...' : 'Lead Magnet speichern'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditContent('');
                  }}
                  className="btn btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Saved Lead Magnets */}
      <div style={{ marginTop: '24px' }}>
        <h2 className="form-section-title">Gespeicherte Lead-Magnete ({leadMagnets.length})</h2>
        {leadMagnets.length === 0 ? (
          <EmptyState
            icon="magnet"
            title="Noch keine Lead-Magnete"
            description="Ein Lead Magnet (Checkliste, Guide, Whitepaper) ist der Gegenwert für eine Kontaktadresse – ideal als CTA unter Beiträgen."
            action={{ label: 'Ersten Lead Magnet erstellen', onClick: () => setShowForm(true) }}
          />
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {leadMagnets.map((magnet) => (
              <div key={magnet.id} className="card" data-focus-id={magnet.id}>
                <div
                  className="card-header"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setExpandedId(expandedId === magnet.id ? null : magnet.id)}
                >
                  <div>
                    <h3 className="card-title">
                      {getTypeIcon(magnet.type)} {magnet.name}
                    </h3>
                    <p className="card-description">
                      <strong>Typ:</strong> {getTypeLabel(magnet.type)}
                      {magnet.targetAudience && (
                        <> | <strong>Zielgruppe:</strong> {magnet.targetAudience}</>
                      )}
                    </p>
                    {magnet.description && (
                      <p style={{ fontSize: '13px', color: 'var(--sec-fg-muted)', marginTop: '4px' }}>
                        {magnet.description}
                      </p>
                    )}
                    <p style={{ fontSize: '12px', color: 'var(--sec-fg-subtle)', marginTop: '4px' }}>
                      Erstellt am {new Date(magnet.createdAt).toLocaleDateString('de-DE')}
                    </p>
                    {(() => {
                      const items: RelatedLinkItem[] = [
                        ...(magnet.hostedUrl ? [{ key: 'url', label: 'Online-Fassung', icon: 'external-link' as const, href: magnet.hostedUrl, title: 'Gehostete Fassung öffnen' }] : []),
                        ...(magnet.strategy ? [{ key: 'strategy', label: `Strategie: ${magnet.strategy.name}`, icon: 'compass' as const, to: '/strategy', title: 'Strategie öffnen' }] : []),
                        ...(magnet.verwendetIn ?? []).map((k) => ({
                          key: `icp-${k.id}`, label: k.name, icon: 'target' as const, to: `/icp-campaigns/${encodeURIComponent(k.id)}`,
                          title: `Wird in der ICP-Kampagne „${k.name}“ genutzt`,
                        })),
                      ];
                      return items.length > 0
                        ? <div className="mt-2" onClick={(e) => e.stopPropagation()}><RelatedLinks items={items} label={`Verknüpft mit ${magnet.name}`} /></div>
                        : null;
                    })()}
                  </div>
                  <span style={{ fontSize: '18px', color: 'var(--sec-fg-subtle)' }}>
                    {expandedId === magnet.id ? '▲' : '▼'}
                  </span>
                </div>

                {expandedId === magnet.id && (
                  <EditableMarkdown
                    content={magnet.content || ''}
                    onSave={async (c) => {
                      await leadMagnetApi.update(magnet.id, { content: c });
                      await loadData();
                    }}
                  />
                )}

                <div className="card-footer">
                  <div className="btn-group">
                    <button onClick={() => handleDelete(magnet.id)} className="btn btn-danger btn-sm">
                      Löschen
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div style={{ marginTop: '30px', padding: '24px', backgroundColor: 'color-mix(in srgb, var(--sec-info) 10%, var(--sec-surface))', borderRadius: '12px', border: '1px solid color-mix(in srgb, var(--sec-info) 30%, var(--sec-surface))' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: 'var(--sec-info)' }}>
          💡 Verwendungstipps
        </h3>
        <ul style={{ fontSize: '14px', color: 'var(--sec-fg-muted)', margin: '0', paddingLeft: '20px' }}>
          <li>Lead-Magnete können als PDF exportiert und auf deiner Website angeboten werden</li>
          <li>Verlinke sie in LinkedIn-Posts, um Downloads und Lead-Generierung zu fördern</li>
          <li>Passe den generierten Inhalt an deine Marke und Tonalität an</li>
          <li>Kombiniere Lead-Magnete mit E-Mail-Follow-ups für maximalen Erfolg</li>
        </ul>
      </div>
    </div>
  );
};

export default LeadMagnetPage;
